import {
  QuestionStudyActivitySubType,
  StudyActivityDifficultyTypes,
  StudyActivityTypes,
} from '@mildang/graphql-artifact';
import { ChipColor } from '@mildang/design-system-legacy/esm/components/molecules/Chip/Chip';
import { I18n } from '@lingui/core';
import { t } from '@lingui/core/macro';

export const StudyActivityTypesValues: StudyActivityTypes[] = [
  'ARTICLE',
  'ENGLISH_SPEAKING',
  'MATH_QUESTION',
  'MEGASTUDY_VIDEO',
  'QUESTION',
  'READING',
  'SENTENCE_COMPLETE_BLOCKS',
  'SENTENCE_COMPLETE_WHOLE_BLOCKS',
  'SENTENCE_GRAMMAR_STUDY',
  'SENTENCE_MATCH_BLANK_BLOCKS',
  'SENTENCE_MATCH_WHOLE_BLOCKS',
  'SENTENCE_ORDER',
  'TODO_LIST',
  'VIDEO',
  'WORD_MEMORY',
  'WORD_TEST',
];

export const STUDYACTIVITY_TYPE_CHIP_COLORS: Record<StudyActivityTypes, ChipColor> = {
  ['QUESTION']: 'warning',
  ['READING']: 'info',
  ['VIDEO']: 'error',
  ['WORD_TEST']: 'success',
  ['WORD_MEMORY']: 'success',
  ['SENTENCE_COMPLETE_BLOCKS']: 'primary',
  ['SENTENCE_COMPLETE_WHOLE_BLOCKS']: 'primary',
  ['SENTENCE_GRAMMAR_STUDY']: 'primary',
  ['SENTENCE_MATCH_BLANK_BLOCKS']: 'primary',
  ['SENTENCE_MATCH_WHOLE_BLOCKS']: 'primary',
  ['SENTENCE_ORDER']: 'primary',
  ['MATH_QUESTION']: 'warning',
  ['TODO_LIST']: 'primary',
  ['MEGASTUDY_VIDEO']: 'error',
  ['ARTICLE']: 'info',
  ['ENGLISH_SPEAKING']: 'info',
};

export const STUDYACTIVITY_TYPE_CHIP_LABELS: (i18n: I18n) => Record<StudyActivityTypes, string> = (i18n) => ({
  ['QUESTION']: t(i18n)`문제`,
  ['MATH_QUESTION']: t(i18n)`수학 문제`,
  ['READING']: t(i18n)`독해`,
  ['VIDEO']: t(i18n)`강의`,
  ['WORD_TEST']: t(i18n)`단어시험`,
  ['WORD_MEMORY']: t(i18n)`단어암기`,
  ['SENTENCE_COMPLETE_BLOCKS']: t(i18n)`암기`,
  ['SENTENCE_COMPLETE_WHOLE_BLOCKS']: t(i18n)`암기`,
  ['SENTENCE_GRAMMAR_STUDY']: t(i18n)`암기`,
  ['SENTENCE_MATCH_BLANK_BLOCKS']: t(i18n)`암기`,
  ['SENTENCE_MATCH_WHOLE_BLOCKS']: t(i18n)`암기`,
  ['SENTENCE_ORDER']: t(i18n)`순서`,
  ['TODO_LIST']: t(i18n)`학습목표`,
  ['MEGASTUDY_VIDEO']: t(i18n)`강의`,
  ['ARTICLE']: t(i18n)`자료`,
  ['ENGLISH_SPEAKING']: t(i18n)`말하기`,
});

export const STUDYACTIVITY_DIFFICULTY_TYPE_LABELS: Record<StudyActivityDifficultyTypes, string> = {
  ['ADVANCED']: '심화',
  ['BASIC']: '개념',
  ['NORMAL']: '발전',
};

export const NON_AUTO_GENERATED_ACTIVITIES: StudyActivityTypes[] = [
  'VIDEO',
  'MEGASTUDY_VIDEO',
  'QUESTION',
  'MATH_QUESTION',
  'ARTICLE',
  'ENGLISH_SPEAKING',
];

export const AUTO_GENERATED_ACTIVITIES: StudyActivityTypes[] = [
  'WORD_TEST',
  'WORD_MEMORY',
  'SENTENCE_COMPLETE_BLOCKS',
  'SENTENCE_COMPLETE_WHOLE_BLOCKS',
  'SENTENCE_GRAMMAR_STUDY',
  'SENTENCE_MATCH_BLANK_BLOCKS',
  'SENTENCE_MATCH_WHOLE_BLOCKS',
  'SENTENCE_ORDER',
  'READING',
];

export const HAS_SA_DETAIL_PAGE_ACTIVITIES: StudyActivityTypes[] = [
  ...NON_AUTO_GENERATED_ACTIVITIES,
  'SENTENCE_COMPLETE_BLOCKS',
  'SENTENCE_COMPLETE_WHOLE_BLOCKS',
  'SENTENCE_GRAMMAR_STUDY',
  'SENTENCE_MATCH_BLANK_BLOCKS',
  'SENTENCE_MATCH_WHOLE_BLOCKS',
  'SENTENCE_ORDER',
  'READING',
];

export type HasSaDetailPageActivityType = Extract<
  StudyActivityTypes,
  | 'VIDEO'
  | 'MEGASTUDY_VIDEO'
  | 'QUESTION'
  | 'MATH_QUESTION'
  | 'SENTENCE_COMPLETE_BLOCKS'
  | 'SENTENCE_COMPLETE_WHOLE_BLOCKS'
  | 'SENTENCE_GRAMMAR_STUDY'
  | 'SENTENCE_MATCH_BLANK_BLOCKS'
  | 'SENTENCE_MATCH_WHOLE_BLOCKS'
  | 'SENTENCE_ORDER'
  | 'READING'
  | 'ARTICLE'
  | 'ENGLISH_SPEAKING'
>;

export enum MarkedType {
  Marked = 'Marked',
  Unmarked = 'Unmarked',
}

export const STUDY_ACTIVITY_SUB_TYPE_LABELS: Record<QuestionStudyActivitySubType, string> = {
  ['COMMON']: '일반',
  ['SELECTING_GRAMMAR_OR_VOCABULARY']: '선택지 고르기',
  ['FILLING_BLANK_TYPING']: '빈칸 채우기',
  ['FILLING_BLANK_CHOICE']: '빈칸 맞추기',
  ['FINDING_REFERRAL_TARGET']: '대상 찾기',
  ['CHART']: '차트',
  ['GRAPH']: '그래프',
};

export const DEFAULT_STUDY_ACTIVITY_TYPE_MAX_STUDY_TIME = {
  ['SENTENCE_MATCH_BLANK_BLOCKS']: 'PT1M',
  ['SENTENCE_MATCH_WHOLE_BLOCKS']: 'PT1M30S',
  ['SENTENCE_COMPLETE_BLOCKS']: 'PT2M30S',
  ['SENTENCE_COMPLETE_WHOLE_BLOCKS']: 'PT2M30S',
  ['SENTENCE_GRAMMAR_STUDY']: 'PT2M30S',
  ['SENTENCE_ORDER']: 'PT3M',
  ['READING']: 'PT3M',
  ['ENGLISH_SPEAKING']: 'PT5M',
  ['COMMON']: 'PT3M',
  ['SELECTING_GRAMMAR_OR_VOCABULARY']: 'PT3M',
  ['FILLING_BLANK_TYPING']: 'PT3M',
  ['FILLING_BLANK_CHOICE']: 'PT3M',
  ['FINDING_REFERRAL_TARGET']: 'PT3M',
};

export const DEFAULT_ENGLISH_SPEAKING_ACTIVITY_RECORD_TIME = 'PT2M';
export const MAX_ENGLISH_SPEAKING_ACTIVITY_RECORD_TIME = 'PT3M';
